import { CurrencyPipe, DecimalPipe } from '@angular/common'
import { HttpParams } from '@angular/common/http'
import { Component } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { WjChartModule } from '@grapecity/wijmo.angular2.chart'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import { NgbNav, NgbNavContent, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase, NgbNavOutlet } from '@ng-bootstrap/ng-bootstrap'

import { BaseComponent, Grid, ListItem, listItemsUnchanged } from '../../shared'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { GridExportComponent } from '../../shared/reusable-components/grid-export/grid-export.component'
import { GridSelectionCalculationComponent } from '../../shared/reusable-components/grid-selection-calculation/grid-selection-calculation.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'
import { EquipmentRatesComponent } from './equipment-rates/equipment-rates.component'
import { JobChargeRatesComponent } from './job-charge-rates/job-charge-rates.component'
import { MaxOverheadRatesComponent } from './max-overhead-rates/max-overhead-rates.component'
import { OverheadRatesComponent } from './overhead-rates/overhead-rates.component'

interface LaborRateRow {
    id: string
    month: Date
    monthLabel: string
    classCode: string
    classDisplay: string
    hours: number
    wageRate: number
    payrollTaxRate: number
    workersCompRate: number
    healthRate: number
    ptoRate: number
    lifeRate: number
    match401kRate: number
    fringeCombinedRate: number
    totalBurdenRate: number
    fullyLoadedRate: number
}

interface LaborSummaryRow {
    id: string
    classCode: string
    classDisplay: string
    hours: number
    wageRate: number
    payrollTaxRate: number
    workersCompRate: number
    fringeCombinedRate: number
    totalBurdenRate: number
    fullyLoadedRate: number
}

interface LaborLiabilityCode {
    code: number
    description: string
    groupName: string
}

interface ChartSeries {
    binding: string
    name: string
}

@Component({
    templateUrl: './bid-rate-data.component.html',
    standalone: true,
    imports: [
        FormsModule,
        CurrencyPipe,
        DecimalPipe,
        NgbNav,
        NgbNavItem,
        NgbNavItemRole,
        NgbNavLink,
        NgbNavLinkBase,
        NgbNavContent,
        NgbNavOutlet,
        WjInputModule,
        WjChartModule,
        WjGridModule,
        WjGridFilterModule,
        AutoSizeDirective,
        ActionToolbarCount,
        ActionToolbarSearchComponent,
        GridExportComponent,
        GridSelectionCalculationComponent,
        EquipmentRatesComponent,
        JobChargeRatesComponent,
        OverheadRatesComponent,
        MaxOverheadRatesComponent,
    ],
})
export class BidRateDataComponent extends BaseComponent {
    activeTab = 1
    unmappedAccountCount = 0
    grid: Grid<LaborRateRow>
    summaryGrid: Grid<LaborSummaryRow>
    summaryRows: LaborSummaryRow[] = []
    rows: LaborRateRow[] = []
    liabilityCodes: LaborLiabilityCode[] = []
    classOptions: ListItem<string>[] = []
    chartPoints: Record<string, Date | number | null>[] = []
    chartSeries: ChartSeries[] = []

    selectedClass = ''
    compareClasses = false
    showBurdenDetail = false
    yesNoOptions: ListItem<boolean>[] = [new ListItem(true, 'Yes'), new ListItem(false, 'No')]
    unallocatedBurden = 0
    rangeInvalid = false

    startDate = new Date(new Date().getFullYear(), 0, 1)
    endDate = new Date(new Date().getFullYear(), new Date().getMonth(), 1)
    minDate = new Date(2012, 0, 1)
    maxDate = new Date()

    private applyingData = false

    constructor() {
        super()
        this.grid = new Grid<LaborRateRow>(this.services, 'id', this)
            .filterBy(item => [item.classDisplay, item.classCode])
            .wrapColumnHeaders()
        this.summaryGrid = new Grid<LaborSummaryRow>(this.services, 'id', this)
            .filterBy(item => [item.classDisplay, item.classCode])
            .wrapColumnHeaders()
        this.grid.selectionChanged.on(item => this.onGridSelection(item))
        this.load()
    }

    tabChanged() {
        setTimeout(() => {
            window.dispatchEvent(new Event('resize'))
        }, 50)
    }

    onRangeChanged() {
        this.rangeInvalid = this.startDate > this.endDate
        if (this.rangeInvalid) return
        this.load()
    }

    onCompareChanged() {
        if (this.compareClasses) {
            const anyChecked = this.classOptions.some(item => item.$checked)
            const selected = this.classOptions.find(item => item.value === this.selectedClass)
            if (selected && !anyChecked) selected.$checked = true
        }
        this.rebuildChart()
    }

    load() {
        const params = new HttpParams().set('startMonth', this.monthParam(this.startDate)).set('endMonth', this.monthParam(this.endDate))
        this.cancelableRequest(
            'laborRates',
            true,
            this.services.data.load(true, true, this.services.data.get('/api/BidRate/GetLaborRates', params)).subscribe({
                next: result => {
                    const payload = result[0] as {
                        rows: any[]
                        liabilityCodes: LaborLiabilityCode[]
                        unallocatedBurden: number
                    }
                    this.liabilityCodes = payload.liabilityCodes ?? []
                    this.unallocatedBurden = payload.unallocatedBurden ?? 0
                    this.rows = (payload.rows ?? []).map(row => this.toRow(row))
                    this.buildSummary()
                    this.syncClassOptions()
                    this.selectedClass = this.pickClass()
                    this.rebuildChart()
                    this.initialLoadingComplete()
                },
                error: () => this.initialLoadingComplete(),
            }),
        )
    }

    get currentRow(): LaborRateRow | undefined {
        const matches = this.rows.filter(row => row.classCode === this.selectedClass)
        return matches.reduce<LaborRateRow | undefined>((latest, row) => (!latest || row.month > latest.month ? row : latest), undefined)
    }

    get currentClassDisplay(): string {
        return this.currentRow?.classDisplay || this.selectedClass
    }

    get checkedClassCount(): number {
        return this.classOptions.filter(item => item.$checked).length
    }

    get liabilityGroups(): { name: string; codes: LaborLiabilityCode[] }[] {
        const groups: { name: string; codes: LaborLiabilityCode[] }[] = []
        for (const code of this.liabilityCodes) {
            let group = groups.find(item => item.name === code.groupName)
            if (!group) {
                group = { name: code.groupName, codes: [] }
                groups.push(group)
            }
            group.codes.push(code)
        }
        return groups
    }

    private onGridSelection(item: LaborRateRow | null) {
        if (this.applyingData || !item?.classCode || item.classCode === this.selectedClass) return
        this.selectedClass = item.classCode
        this.compareClasses = false
        this.rebuildChart()
    }

    private toRow(row: any): LaborRateRow {
        const [year, month] = String(row.month).split('-').map(value => Number(value))
        const monthDate = new Date(year, month - 1, 1)
        return {
            id: `${row.month}|${row.classCode}`,
            month: monthDate,
            monthLabel: monthDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
            classCode: row.classCode,
            classDisplay: row.classDisplay,
            hours: row.hours,
            wageRate: row.wageRate,
            payrollTaxRate: row.payrollTaxRate,
            workersCompRate: row.workersCompRate,
            healthRate: row.healthRate,
            ptoRate: row.ptoRate,
            lifeRate: row.lifeRate,
            match401kRate: row.match401kRate,
            fringeCombinedRate: row.fringeCombinedRate,
            totalBurdenRate: row.totalBurdenRate,
            fullyLoadedRate: row.fullyLoadedRate,
        }
    }

    private buildSummary() {
        const totals = new Map<string, { classDisplay: string; hours: number; wage: number; payrollTax: number; workersComp: number; health: number; pto: number; life: number; match: number }>()
        for (const row of this.rows) {
            let acc = totals.get(row.classCode)
            if (!acc) {
                acc = { classDisplay: row.classDisplay, hours: 0, wage: 0, payrollTax: 0, workersComp: 0, health: 0, pto: 0, life: 0, match: 0 }
                totals.set(row.classCode, acc)
            }
            const hours = row.hours || 0
            acc.hours += hours
            acc.wage += row.wageRate * hours
            acc.payrollTax += row.payrollTaxRate * hours
            acc.workersComp += row.workersCompRate * hours
            acc.health += row.healthRate * hours
            acc.pto += row.ptoRate * hours
            acc.life += row.lifeRate * hours
            acc.match += row.match401kRate * hours
        }

        this.summaryRows = [...totals.entries()]
            .map(([classCode, acc]) => {
                const perHour = (amount: number) => (acc.hours ? amount / acc.hours : 0)
                const wageRate = perHour(acc.wage)
                const payrollTaxRate = perHour(acc.payrollTax)
                const workersCompRate = perHour(acc.workersComp)
                const fringeCombinedRate = perHour(acc.health) + perHour(acc.pto) + perHour(acc.life) + perHour(acc.match)
                const totalBurdenRate = payrollTaxRate + workersCompRate + fringeCombinedRate
                return {
                    id: classCode,
                    classCode,
                    classDisplay: acc.classDisplay,
                    hours: acc.hours,
                    wageRate,
                    payrollTaxRate,
                    workersCompRate,
                    fringeCombinedRate,
                    totalBurdenRate,
                    fullyLoadedRate: wageRate + totalBurdenRate,
                }
            })
            .sort((a, b) => a.classCode.localeCompare(b.classCode))
        this.summaryGrid.items = this.summaryRows
    }

    private syncClassOptions() {
        const byCode = new Map<string, string>()
        for (const row of this.rows) {
            if (!byCode.has(row.classCode)) byCode.set(row.classCode, row.classDisplay)
        }
        const next = [...byCode.entries()]
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([code, display]) => {
                const item = new ListItem(code, display)
                item.$checked = this.classOptions.find(current => current.value === code)?.$checked ?? false
                return item
            })
        if (!listItemsUnchanged(this.classOptions, next)) this.classOptions = next
    }

    private pickClass(): string {
        if (this.selectedClass && this.rows.some(row => row.classCode === this.selectedClass)) return this.selectedClass
        if (this.rows.length === 0) return ''
        const latest = this.rows.reduce((max, row) => (row.month > max ? row.month : max), this.rows[0].month)
        const inLatest = this.rows.filter(row => row.month.getTime() === latest.getTime())
        inLatest.sort((a, b) => b.hours - a.hours || a.classCode.localeCompare(b.classCode))
        return inLatest[0].classCode
    }

    rebuildChart() {
        this.applyGridFilter()
        const months = this.monthsInRange()
        if (this.compareClasses) {
            const selected = this.classOptions.filter(item => item.$checked).slice(0, 5)
            this.chartSeries = selected.map((item, index) => ({ binding: `class${index}`, name: item.display }))
            this.chartPoints = months.map(month => {
                const point: Record<string, Date | number | null> = { month }
                selected.forEach((item, index) => {
                    point[`class${index}`] = this.rowFor(item.value, month)?.fullyLoadedRate ?? null
                })
                return point
            })
            return
        }

        const detailSeries: ChartSeries[] = [
            { binding: 'wageRate', name: 'Wage' },
            { binding: 'payrollTaxRate', name: 'Payroll Taxes' },
            { binding: 'workersCompRate', name: 'Workers Comp' },
            { binding: 'healthRate', name: 'Health' },
            { binding: 'ptoRate', name: 'PTO' },
            { binding: 'lifeRate', name: 'Life / DisA / EAP' },
            { binding: 'match401kRate', name: '401k Match' },
            { binding: 'fullyLoadedRate', name: 'Fully Loaded' },
        ]
        const summarySeries: ChartSeries[] = [
            { binding: 'wageRate', name: 'Wage' },
            { binding: 'totalBurdenRate', name: 'Total Burden' },
            { binding: 'fullyLoadedRate', name: 'Fully Loaded' },
        ]
        this.chartSeries = this.showBurdenDetail ? detailSeries : summarySeries
        this.chartPoints = months.map(month => {
            const row = this.rowFor(this.selectedClass, month)
            return {
                month,
                wageRate: row?.wageRate ?? null,
                totalBurdenRate: row?.totalBurdenRate ?? null,
                fullyLoadedRate: row?.fullyLoadedRate ?? null,
                payrollTaxRate: row?.payrollTaxRate ?? null,
                workersCompRate: row?.workersCompRate ?? null,
                healthRate: row?.healthRate ?? null,
                ptoRate: row?.ptoRate ?? null,
                lifeRate: row?.lifeRate ?? null,
                match401kRate: row?.match401kRate ?? null,
            }
        })
    }

    private applyGridFilter() {
        const visible = this.rowsForGrid()
        this.applyingData = true
        this.grid.items = visible
        setTimeout(() => {
            this.applyingData = false
        }, 50)
    }

    private rowsForGrid(): LaborRateRow[] {
        if (this.compareClasses) {
            const checked = new Set(this.classOptions.filter(item => item.$checked).map(item => item.value))
            return this.rows.filter(row => checked.has(row.classCode))
        }
        if (!this.selectedClass) return []
        return this.rows.filter(row => row.classCode === this.selectedClass)
    }

    private rowFor(classCode: string, month: Date): LaborRateRow | undefined {
        return this.rows.find(row => row.classCode === classCode && row.month.getTime() === month.getTime())
    }

    private monthsInRange(): Date[] {
        const months: Date[] = []
        const cursor = new Date(this.startDate.getFullYear(), this.startDate.getMonth(), 1)
        const end = new Date(this.endDate.getFullYear(), this.endDate.getMonth(), 1)
        while (cursor <= end) {
            months.push(new Date(cursor))
            cursor.setMonth(cursor.getMonth() + 1)
        }
        return months
    }

    private monthParam(date: Date): string {
        const month = String(date.getMonth() + 1).padStart(2, '0')
        return `${date.getFullYear()}-${month}-01`
    }
}
